import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user, api } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) { setCart([]); return; }
    try {
      setLoading(true);
      const res = await api.get('/api/cart');
      setCart(res.data);
    } catch {
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  }, [user, api]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productId, quantity, options = {}, designFileId = null) => {
    try {
      await api.post('/api/cart', { product_id: productId, quantity, options, design_file_id: designFileId });
      await fetchCart();
      return true;
    } catch {
      toast.error('Failed to add to cart');
      return false;
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      await api.put(`/api/cart/${itemId}?quantity=${quantity}`);
      await fetchCart();
    } catch {
      toast.error('Failed to update cart');
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await api.delete(`/api/cart/${itemId}`);
      await fetchCart();
    } catch {
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    try {
      await api.delete('/api/cart');
      setCart([]);
    } catch {
      toast.error('Failed to clear cart');
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateCartItem, removeFromCart, clearCart, fetchCart, cartTotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
